import { useState, createContext, useEffect, useMemo } from "react";
import axiosAdmin from "../../../api/apiAdmin";

export const AdminPrimaryContext = createContext(null);

/**
 * A warehouse-scoped admin (admin.abroad_office_id set on the backend) can only
 * see and mutate their own warehouse. The backend enforces this on every endpoint,
 * but for a coherent UI we also narrow the option lists (abroad offices, countries,
 * flights) so dropdowns don't offer choices that would 403.
 */
export const AdminContext = ({ children }) => {
  const [user, setUser] = useState({});
  const [token, _setToken] = useState(localStorage.getItem("MGL_TOKEN_ADM"));
  const [isAuthorized, setIsAuthorized] = useState(false);

  // Raw API responses. `user` decides whether we expose the full list or a
  // warehouse-scoped subset (see the useMemo derivations below).
  const [rawCountries, setRawCountries] = useState([]);
  const [rawAbOffices, setRawAbOffices] = useState([]);
  const [geBranches, setGeBranches] = useState([]);
  const [rawFlights, setRawFlights] = useState([]);

  const setToken = (token) => {
    _setToken(token);
    if (token) {
      localStorage.setItem("MGL_TOKEN_ADM", token);
    } else {
      localStorage.removeItem("MGL_TOKEN_ADM");
    }
  };

  // Warehouse the current admin is scoped to. null = global admin (sees everything).
  const scopedOfficeId = user?.abroad_office_id
    ? Number(user.abroad_office_id)
    : null;

  // Filtered lists exposed to consumers.
  const abOffices = useMemo(
    () =>
      scopedOfficeId
        ? rawAbOffices.filter((o) => Number(o.id) === scopedOfficeId)
        : rawAbOffices,
    [rawAbOffices, scopedOfficeId]
  );

  const countries = useMemo(() => {
    if (!scopedOfficeId) return rawCountries;
    const allowed = new Set(abOffices.map((o) => Number(o.country_id)));
    return rawCountries.filter((c) => allowed.has(Number(c.id)));
  }, [rawCountries, abOffices, scopedOfficeId]);

  const flights = useMemo(() => {
    if (!scopedOfficeId) {
      // Legacy shape: consumers expect an array of shipment codes (strings).
      return rawFlights.map((f) =>
        typeof f === "string" ? f : f.code
      );
    }
    return rawFlights
      .filter(
        (f) =>
          typeof f === "object" &&
          f !== null &&
          Number(f.abroad_office_id) === scopedOfficeId
      )
      .map((f) => f.code);
  }, [rawFlights, scopedOfficeId]);

  // Load current admin.
  useEffect(() => {
    axiosAdmin
      .get("/admin")
      .then(({ data }) => {
        setUser(data);
        setIsAuthorized(true);
      })
      .catch(() => {});
  }, []);

  // Load abroad offices whenever auth state flips.
  useEffect(() => {
    if (!isAuthorized) return;
    axiosAdmin
      .get("/abroadOffice")
      .then(({ data }) => {
        setRawAbOffices(data);
      })
      .catch(() => {});
  }, [isAuthorized]);

  // Countries + Georgian branches + shipments load once we know who the admin is
  // (so scoped filtering can be applied).
  useEffect(() => {
    axiosAdmin
      .get("/countries")
      .then(({ data }) => setRawCountries(data))
      .catch(() => {});

    // Ask the server to pre-filter shipments to the admin's warehouse when scoped.
    // Store the FULL shipment objects (not just codes) so the useMemo can filter
    // reliably even if the server ever returns a superset.
    const shipParams = scopedOfficeId
      ? `?abroad_office_id=${scopedOfficeId}&per_page=500`
      : `?per_page=500`;
    axiosAdmin
      .get(`/shipments${shipParams}`)
      .then(({ data }) => {
        setRawFlights(
          (data.data || []).map((item) => ({
            code: item.code,
            abroad_office_id: item.abroad_office_id,
          }))
        );
      })
      .catch(() => {});

    axiosAdmin
      .get(`/offices`)
      .then(({ data }) => {
        const tmp = data.map((item) => ({
          name: item.city,
          value: item.id,
        }));
        setGeBranches(tmp);
      })
      .catch(() => {});
  }, [user, scopedOfficeId]);

  return (
    <AdminPrimaryContext.Provider
      value={{
        user,
        setUser,
        token,
        setToken,
        isAuthorized,
        setIsAuthorized,
        countries,
        // `foreignAdresses` is a legacy alias for abOffices used by some pages.
        foreignAdresses: abOffices,
        abOffices,
        flights,
        setFlights: setRawFlights,
        geBranches,
        // Expose the scope so individual screens can preselect / lock inputs.
        scopedOfficeId,
      }}
    >
      {children}
    </AdminPrimaryContext.Provider>
  );
};
